import {
  AfterViewInit,
  Component,
  Input,
  OnDestroy,
  signal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'siat-scroll-to-top',
  standalone: true,
  imports: [MatButtonModule, MatIconModule],
  template: `
    @if (isVisible()) {
      <button
        mat-fab
        extended
        class="scroll-to-top-btn"
        aria-label="Torna su"
        (click)="scrollToTop()"
      >
        <mat-icon>arrow_upward</mat-icon>
        Torna su
      </button>
    }
  `,
  styles: [
    `
      :host {
        position: fixed;
        bottom: 24px;
        right: 24px;
        z-index: 200;
      }
    `,
  ],
})
export class ScrollToTopComponent implements AfterViewInit, OnDestroy {
  /**
   * Selector for the scroll container. The component will listen to scroll events on this container to determine when to show the "scroll to top" button. Defaults to 'mat-sidenav-content', which is common in Angular Material layouts, but can be customized as needed.
   */
  @Input()
  scrollContainerSelector = 'mat-sidenav-content';

  /**
   * Signal to track the visibility of the "scroll to top" button. The button becomes visible when the user scrolls down more than a certain threshold (300px by default) from the top of the scroll container.
   */
  isVisible = signal(false);

  /**
   * Reference to the scroll container element and the scroll event handler. These are used to manage event listeners and ensure proper cleanup when the component is destroyed.
   */
  private scrollContainer: HTMLElement | null = null;

  /**
   * Scroll event handler function that updates the visibility of the "scroll to top" button based on the current scroll position of the container. It checks if the scrollTop value exceeds a predefined threshold and updates the isVisible signal accordingly.
   */
  private scrollHandler: () => void;

  /**
   * Threshold in pixels that determines when the "scroll to top" button should be shown. If the user scrolls down more than this distance from the top of the scroll container, the button will become visible. This value can be adjusted as needed to provide a better user experience based on the specific layout and content of the application.
   */
  private static readonly SHOW_THRESHOLD = 300;

  /**
   * @inheritdoc
   */
  ngAfterViewInit(): void {
    this.scrollContainer = document.querySelector(this.scrollContainerSelector);
    this.scrollHandler = () => {
      this.isVisible.set(
        (this.scrollContainer?.scrollTop ?? 0) >
          ScrollToTopComponent.SHOW_THRESHOLD
      );
    };
    this.scrollContainer?.addEventListener('scroll', this.scrollHandler, {
      passive: true,
    });
  }

  /**
   * @inheritdoc
   */
  ngOnDestroy(): void {
    this.scrollContainer?.removeEventListener('scroll', this.scrollHandler);
  }

  /**
   * Scrolls the container back to the top smoothly when the "scroll to top" button is clicked. This method uses the scrollTo function with smooth behavior to provide a better user experience. It checks if the scrollContainer reference is valid before attempting to scroll, ensuring that the method does not throw errors if the container is not found or has been removed from the DOM.
   */
  scrollToTop(): void {
    this.scrollContainer?.scrollTo({ top: 0, behavior: 'smooth' });
  }
}
